import { Flex, FlexProps } from '@chakra-ui/react'
import { MDXEditor, MDXEditorMethods, MDXEditorProps } from '@mdxeditor/editor'
import '@mdxeditor/editor/style.css'
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react'
import { useDebounceCallback } from 'usehooks-ts'
import './markdownStyle.css'
import { encodeMarkdown, makePlugins, parseMarkdown } from './markdownUtils'

export interface Props extends Omit<MDXEditorProps, 'markdown' | 'onChange'> {
  containerProps?: Omit<FlexProps, 'children'>
  debounce?: number
  hideBlockTypes?: boolean
  lineClamp?: number
  onChange?: (value: string) => void
  tagText?: boolean
  value?: string
}
export type Methods = MDXEditorMethods

const MarkdownEditor = forwardRef<Methods, Props>(
  (
    {
      containerProps,
      debounce,
      hideBlockTypes,
      lineClamp,
      onChange,
      readOnly,
      tagText,
      value,
      ...rest
    },
    ref
  ) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const editorRef = useRef<MDXEditorMethods>(null)

    useImperativeHandle(ref, () => editorRef.current!, [])

    useEffect(() => {
      if (!editorRef.current || value === encodeMarkdown(editorRef.current.getMarkdown())) return
      editorRef.current.setMarkdown(parseMarkdown(value))
    }, [value])

    const debounced = useDebounceCallback(v => onChange?.(v), 500)
    const plugins = makePlugins(readOnly, hideBlockTypes, tagText)

    return (
      <Flex
        ref={containerRef}
        {...(!readOnly && { border: '1px solid rgb(226, 232, 240)', borderRadius: 'sm' })}
        overflow="hidden"
        css={{
          '& .mdxeditor-contenteditable': {
            ...(readOnly && {
              padding: 0,
            }),
            ...(lineClamp && {
              display: '-webkit-box',
              WebkitLineClamp: `${lineClamp}`,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }),
          },
        }}
        {...containerProps}
      >
        <MDXEditor
          readOnly={readOnly}
          overlayContainer={containerRef.current}
          ref={editorRef}
          plugins={plugins}
          contentEditableClassName="mdxeditor-contenteditable"
          markdown={parseMarkdown(value)}
          onChange={v => {
            const encoded = encodeMarkdown(v)
            if (debounce) debounced(encoded)
            else onChange?.(encoded)
          }}
          {...rest}
        />
      </Flex>
    )
  }
)

export default MarkdownEditor
